import { Box, xcss } from '@atlaskit/primitives';
import TextArea from '@atlaskit/textarea';
import { token } from '@atlaskit/tokens';
import { SeamlessTextArea } from '@databases/form/FormComponents';
import { Metrics } from '@databases/frontend-metrics';
import { useTypingPerformance } from '@databases/frontend-metrics/useTypingPerformance';
import { useTextDelta } from '@databases/react/useTextDelta';
import { defaultStoreUtils } from '@databases/redux';
import { selectFieldIdsByContentId } from '@databases/redux/database-view';
import composeRefs from '@seznam/compose-react-refs';
import { type Op } from 'quill-delta';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { type TextLikeFieldType } from '../types';

import {
  MAX_POPOVER_EDIT_WIDTH,
  TEXT_VALUE_MAX_TEXT_AREA_HEIGHT,
  useAdjustTextAreaDimensions
} from './popover-edit-common';
import { createTextDeltaChange } from './text-value-change';
import { type EditComponentProps } from './types';

const textAreaContainerStyling = xcss({
  display: 'flex',
  flexDirection: 'row',
  boxSizing: 'border-box',
  position: 'relative',
  zIndex: 'dialog' // Ensure container sits above blue dots
});

const lastFieldStylings = xcss({
  flexDirection: 'row-reverse'
});

const textAreaMinHeightStyling = xcss({
  minHeight: 'size.400'
});

function TextValuePopoverEdit<Type extends TextLikeFieldType>({
  field,
  onChange,
  onFocus,
  onBlur,
  autoFocus,
  value,
  isSeamless,
  isRenderedInSidePanel,
  contentId,
  isBulkEdit,
  ariaLabel,
  initialKey,
  'aria-describedby': ariaDescribedBy
}: EditComponentProps<Type>) {
  const maxWidthReachedRef = useRef<boolean>(false);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [inputValue, setInputValue] = useState(initialKey ?? value.plain);
  const store = defaultStoreUtils.useStore();

  const adjustTextAreaDimensions = useAdjustTextAreaDimensions({
    textAreaRef,
    maxWidthReachedRef,
    maxPopoverEditWidth: isRenderedInSidePanel
      ? textAreaRef.current?.scrollWidth
      : MAX_POPOVER_EDIT_WIDTH
  });

  useEffect(() => {
    adjustTextAreaDimensions();
  }, [inputValue, adjustTextAreaDimensions]);

  useEffect(() => {
    if (initialKey !== undefined) {
      const oldLength = value.plain.length;
      const delta: Op[] =
        oldLength > 0
          ? [{ delete: oldLength }, { insert: initialKey }]
          : [{ insert: initialKey }];
      onChange({ plain: initialKey }, createTextDeltaChange(field.type, delta));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fieldIds = selectFieldIdsByContentId(store.getState(), {
    contentId
  });

  const isLastField = fieldIds.indexOf(field.id) === fieldIds.length - 1;

  const Component = isSeamless ? SeamlessTextArea : TextArea;

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLTextAreaElement>) => {
      // Put cursor to the end of the field
      e.currentTarget.setSelectionRange(
        e.currentTarget.value.length,
        e.currentTarget.value.length
      );

      adjustTextAreaDimensions();
      onFocus?.(e);
    },
    [onFocus, adjustTextAreaDimensions]
  );

  useTextDelta(
    textAreaRef.current,
    useCallback(
      (delta: Op[], newValue: string) => {
        setInputValue(newValue);
        onChange({ plain: newValue }, createTextDeltaChange(field.type, delta));
      },
      [field.type, onChange]
    )
  );

  const typingPerformanceRef = useTypingPerformance<HTMLTextAreaElement>(
    Metrics.MEAN_TEXT_VALUE_TYPING
  );
  return (
    <Box
      testId={`text-value-edit-expand-${isLastField ? 'left' : 'right'}`}
      xcss={[
        textAreaContainerStyling,
        isLastField && lastFieldStylings,
        isRenderedInSidePanel && textAreaMinHeightStyling
      ]}
    >
      <Component
        value={inputValue}
        onFocus={handleFocus}
        onBlur={onBlur}
        autoFocus={autoFocus}
        resize="none"
        wrap="off"
        ref={composeRefs(textAreaRef, typingPerformanceRef)}
        testId={'text-value-edit'}
        aria-describedby={ariaDescribedBy}
        style={{
          border: `${token('border.width.selected', '2px')} solid ${token(
            'color.background.accent.blue.subtle',
            '#579DFF'
          )}`,
          borderRadius: 'radius.small',
          overflow: 'hidden',
          position: isRenderedInSidePanel || isBulkEdit ? 'relative' : 'absolute',
          boxSizing: 'border-box',
          maxHeight: `${TEXT_VALUE_MAX_TEXT_AREA_HEIGHT}px`,
          // Override @atlaskit/textarea's font.body.large to match the cell's 14px font size.
          font: token('font.body', '14px')
        }}
        aria-label={ariaLabel}
      />
    </Box>
  );
}

export default TextValuePopoverEdit;
